import { useState, useEffect } from "react";
import { cachingPanelData } from "@containers/CompanyProfile/utils/enum.js";
import commaFormat from "@utils/commaFormat";
import { displayCount, getTitleCase } from "@utils/utils";
import ReactCountryFlag from "react-country-flag";
import { useSelector } from "react-redux";
import Pagination from "react-bootstrap/Pagination";
import LoadingSkeleton from "@shared/LoadingSkeleton";
import { trademoTrackingEvents } from "@core/global/trademo-tracking";
import trackingEventDictionary from "@core/dictionary/tracking-event";
import { generateTrackingObject } from "@core/global/global-country-functions";
import EmptyStateTradeOverview from "./EmptyStateTradeOverview";

const Ports = ({ activeCategData, pathname, companyName, dataSource, userActivePlan, profileData, isLogisticsProfile, activeCategory, activePanelId, headings, toggleExpand, expandedItems, lastPage, loader, fetchTradeOverview }) => {
  const countDetails = useSelector((state) => state.companyProfile.countDetails);
  const category_count = countDetails[activeCategData?.countEnum];
  // These are pagination states
  const [currentPage, setCurrentPage] = useState(lastPage);
  const itemsPerPage = 10;
  const [paginatedData, setPaginatedData] = useState([]);
  const totalPages = Math.ceil(category_count / itemsPerPage);

  useEffect(() => {
    if (profileData[activeCategory]) {
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      setPaginatedData(profileData[activeCategory].slice(startIndex, endIndex));
    }
  }, [currentPage, profileData, activeCategory]);

  const handlePageChange = async (direction) => {
    if (direction === "next" && currentPage < totalPages) {
      trademoTrackingEvents(trackingEventDictionary.companyProfilePageTradeOverviewPaginationForwardButton, generateTrackingObject(dataSource, pathname, userActivePlan));
      if (currentPage * itemsPerPage >= profileData[activeCategory].length) {
        await fetchTradeOverview(currentPage);
      }
      setCurrentPage(currentPage + 1);
    } else if (direction === "prev" && currentPage > 1) {
      trademoTrackingEvents(trackingEventDictionary.companyProfilePageTradeOverviewPaginationBackwardButton, generateTrackingObject(dataSource, pathname, userActivePlan));

      setCurrentPage(currentPage - 1);
    }
  };

  const renderPagination = () => {
    const startItem = (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPage * itemsPerPage, category_count);
    const showPagination = !(currentPage * itemsPerPage >= category_count && currentPage === 1);

    return (
      <div className="tw-flex tw-justify-center tw-items-center tw-mt-4">
        <span>
          Showing {startItem}-{endItem} of
        </span>
        <span className="tw-ml-1">
          <Pagination>
            <Pagination.Item className="page-item pagination-item">{category_count}</Pagination.Item>
            {showPagination && (
              <>
                <Pagination.Prev disabled={currentPage === 1} className="page-item pagination-prev" onClick={() => handlePageChange("prev")} />
                <Pagination.Next disabled={currentPage * itemsPerPage >= category_count} className="page-item pagination-next" onClick={() => handlePageChange("next")} />
              </>
            )}
          </Pagination>
        </span>
      </div>
    );
  };

  return loader ? (
    <LoadingSkeleton lines={6} lineHeight="35px" width="100%" shape="rectangle" />
  ) : paginatedData?.length > 0 ? (
    <>
      <span className="tw-font-medium tw-text-gray-900">{`${activePanelId === "importers" && activeCategory === "port" ? "Ports " + companyName + " Shipped from" : activePanelId === "exporters" && activeCategory === "port" ? "Ports " + companyName + " Shipped to" : ""}`}</span>
      {paginatedData?.map((item) => {
        const activeHeading = headings.find((heading) => heading.id === activeCategData?.id && activePanelId === heading.activePanelId);
        const itemKey = `${item.s_no}-${activePanelId}-${activeCategData.id}`;
        const isExpanded = expandedItems.includes(itemKey);
        return (
          item.key && (
            <div key={item.s_no} className={`tw-flex tw-font-semibold tw-cursor-pointer ${!isExpanded ? "tw-overflow-hidden" : "tw-bg-gray-100"} tw-border tw-rounded-lg tw-px-4 tw-pt-3 tw-pb-2`} onClick={() => toggleExpand(itemKey)}>
              <div className="tw-flex tw-flex-col tw-w-full tw-gap-2">
                <div className="tw-flex tw-justify-between">
                  <div className="tw-flex tw-flex-col tw-gap-2 tw-font-semibold tw-w-[50%]">
                    <div className={`${isExpanded ? "tw-flex tw-flex-col tw-gap-1" : "tw-flex tw-gap-1"}`}>
                      <div className="tw-flex tw-gap-1 tw-text-gray-900">
                        <div>{getTitleCase(item[activeCategData?.key1secondDiv])},</div>
                        <div className="tw-flex tw-items-center tw-gap-1">
                          <ReactCountryFlag className="tw-w-4 tw-h-3" countryCode={item[activeCategData?.key3secondDiv]} svg />
                          {getTitleCase(item[activeCategData?.key2secondDiv])}
                        </div>
                      </div>
                    </div>
                  </div>
                  {activeCategData?.isExpandable && (
                    <div
                      className="tw-cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleExpand(itemKey);
                      }}>
                      {(item[activeCategData.key2]?.length >= 1 || item[activeCategData.key1]?.length >= 1) && <span className={`material-symbols-outlined tw-w-5 tw-h-5 tw-border tw-bg-white tw-border-gray-300 tw-rounded-[4px] tw-flex tw-items-center tw-justify-center`}>{isExpanded ? "collapse_content" : "expand_content"}</span>}
                    </div>
                  )}
                </div>

                <div className="tw-flex">
                  <div className="tw-flex tw-w-full">
                    <div className="tw-flex tw-flex-col tw-w-[66%]">
                      <span className="tw-font-normal tw-text-gray-700">
                        {activeHeading.heading2} {activeCategory === "trade_country" && getTitleCase(item.key)}
                      </span>
                      <div className="tw-font-medium tw-text-gray-900">{commaFormat(item[activeCategData?.key1])}</div>
                    </div>
                    <div className="tw-flex tw-flex-col tw-font-medium tw-text-gray-900">
                      <span className="tw-font-normal tw-text-gray-700">{isLogisticsProfile ? activeHeading.heading3Alt : activeHeading.heading3}</span>
                      {item[activeCategData.countEnum2] > 0 ? ` ${item[activeCategData?.countEnum2] + " " + activeCategData.value}` : "NA"}
                    </div>
                  </div>
                </div>

                {isExpanded && <div className="tw-w-full tw-bg-gray-300 tw-border"></div>}
                {isExpanded && (
                  <div className="tw-flex tw-text-sm tw-font-normal tw-text-gray-700">
                    <div className="tw-w-[50%]">{activeHeading.headingL}</div>
                    <div className="tw-ml-[15.5%]">{activeHeading.headingR}</div>
                  </div>
                )}
                <div className={`tw-flex ${item[isLogisticsProfile ? activeCategData?.key2Alt : activeCategData?.key2].length > 3 && isExpanded ? "tw-h-[150px] tw-overflow-scroll" : ""}`}>
                  {isExpanded && (
                    <div className="tw-flex tw-flex-col tw-font-medium tw-text-gray-900 tw-w-[50%]">
                      <div>{getTitleCase(item[activeCategData?.key1secondDiv])},</div>
                      <div className="tw-flex tw-items-center tw-gap-1 tw-font-medium">
                        <ReactCountryFlag className="tw-w-4 tw-h-3" countryCode={item[activeCategData?.key3secondDiv]} svg />
                        {getTitleCase(item[activeCategData?.key2secondDiv])}
                      </div>
                    </div>
                  )}
                  <div className="tw-flex tw-w-full">
                    <div className="tw-flex tw-flex-col tw-gap-2 tw-w-[49%]">
                      {isExpanded &&
                        item[isLogisticsProfile ? activeCategData?.key2Alt : activeCategData?.key2].map((subItem, index) => (
                          <div key={index} className="tw-flex tw-justify-between">
                            <div className="tw-flex tw-flex-col tw-items-center tw-font-medium tw-text-gray-900 tw-text-xs">
                              {displayCount(subItem[activeCategData.key1], 2) + " " + "Shipments"}
                              {activePanelId === cachingPanelData.importers ? (
                                <svg width="93" height="20" viewBox="0 0 93 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M92.0205 10.3536C92.2158 10.1583 92.2158 9.84171 92.0205 9.64645L88.8386 6.46447C88.6433 6.2692 88.3267 6.2692 88.1315 6.46447C87.9362 6.65973 87.9362 6.97631 88.1315 7.17157L90.9599 10L88.1315 12.8284C87.9362 13.0237 87.9362 13.3403 88.1315 13.5355C88.3267 13.7308 88.6433 13.7308 88.8386 13.5355L92.0205 10.3536ZM91.667 9.5L0.666992 9.49999L0.666992 10.5L91.667 10.5L91.667 9.5Z" fill="#667085" />
                                </svg>
                              ) : (
                                <svg width="163" height="20" viewBox="0 0 163 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M35.3125 9.64645C35.1172 9.84171 35.1172 10.1583 35.3125 10.3536L38.4944 13.5355C38.6897 13.7308 39.0063 13.7308 39.2015 13.5355C39.3968 13.3403 39.3968 13.0237 39.2015 12.8284L36.3731 10L39.2015 7.17157C39.3968 6.97631 39.3968 6.65973 39.2015 6.46447C39.0063 6.2692 38.6897 6.2692 38.4944 6.46447L35.3125 9.64645ZM35.666 10.5L126.666 10.5L126.666 9.50001L35.666 9.5L35.666 10.5Z" fill="#667085" />
                                </svg>
                              )}
                            </div>
                          </div>
                        ))}
                    </div>
                    <div className="tw-flex tw-flex-col tw-gap-2">
                      {isExpanded &&
                        item[isLogisticsProfile ? activeCategData?.key2Alt : activeCategData?.key2].map((subItem, index) => (
                          <div key={index} className="tw-flex tw-justify-between tw-text-gray-900 tw-font-medium">
                            <div>
                              {getTitleCase(subItem.port_name)}
                              <div className="tw-flex tw-items-center tw-gap-1 tw-font-medium">
                                <ReactCountryFlag className="tw-w-4 tw-h-3" countryCode={subItem[activeCategData?.key3secondDiv]} svg />
                                {getTitleCase(subItem[activeCategData?.key2secondDiv])}
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )
        );
      })}
      {category_count > 3 && renderPagination()}
    </>
  ) : (
    <EmptyStateTradeOverview description="No Ports Found" />
  );
};

export default Ports;
